'use client';
import { useState } from 'react';
import type { VelocityRow, FitRow } from '@/lib/research/types';
import { TrajectoryChart } from './TrajectoryChart';
import { PortfolioFitPanel } from './PortfolioFitPanel';

interface Props {
  rows: VelocityRow[];
  fitBySymbol: Map<string, FitRow>;
  portfolio: Set<string>;
  rankBy: 'accel5' | 'd5';
}

interface Event {
  ts: string;
  tier: string;
  type: string;
  symbol: string;
  headline: string;
  sourceUrl?: string;
}

const fmtSigned = (v: number | null | undefined, d = 2) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

const tierColor: Record<string, string> = {
  'Q-direct': 'text-amber-400',
  'Q-flow': 'text-blue-400',
  'FYI': 'text-[#94a3b8]',
};

export function MoversTable({ rows, fitBySymbol, portfolio, rankBy }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [events, setEvents] = useState<Map<string, Event[]>>(new Map());

  const toggle = (sym: string) => {
    if (expanded === sym) {
      setExpanded(null);
      return;
    }
    setExpanded(sym);
    if (!events.has(sym)) {
      fetch(`/api/research-events?symbol=${sym}&days=14`)
        .then(r => r.json())
        .then((d: { events: Event[] }) => {
          setEvents(prev => new Map(prev).set(sym, d.events || []));
        })
        .catch(() => {
          setEvents(prev => new Map(prev).set(sym, []));
        });
    }
  };

  return (
    <table className="w-full text-[12px] border-collapse">
      <thead>
        <tr className="text-[10px] text-[#64748b] uppercase tracking-wide border-b border-[#1e293b]">
          <th className="text-left py-2 pl-3 pr-2 w-8">#</th>
          <th className="text-left py-2 px-2">Symbol</th>
          <th className="text-left py-2 px-2">Sector</th>
          <th className="text-right py-2 px-2">Q</th>
          <th className="text-right py-2 px-2">V</th>
          <th className="text-right py-2 px-2">Comp</th>
          <th className="text-right py-2 px-2">Δ5d</th>
          <th className="text-right py-2 px-2">Accel</th>
          <th className="text-right py-2 px-2">Q-share</th>
          <th className="text-right py-2 px-2 pr-3">Streak</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const inP = portfolio.has(r.symbol);
          const isExpanded = expanded === r.symbol;
          const sortVal = rankBy === 'accel5' ? r.accel5 : r.d5.dcomp;
          return (
            <RowFragment
              key={r.symbol}
              row={r}
              rank={i + 1}
              inPortfolio={inP}
              expanded={isExpanded}
              onToggle={() => toggle(r.symbol)}
              sortVal={sortVal}
              events={events.get(r.symbol)}
              fit={fitBySymbol.get(r.symbol) || null}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function RowFragment({
  row, rank, inPortfolio, expanded, onToggle, sortVal, events, fit,
}: {
  row: VelocityRow;
  rank: number;
  inPortfolio: boolean;
  expanded: boolean;
  onToggle: () => void;
  sortVal: number | null;
  events: Event[] | undefined;
  fit: FitRow | null;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer border-b border-[#1e293b] hover:bg-[#111827] transition-colors ${expanded ? 'bg-[#111827]' : ''}`}
      >
        <td className="py-2 pl-3 pr-2 text-[#64748b] tabular-nums">{rank}</td>
        <td className="py-2 px-2">
          <span className={`font-mono font-semibold ${inPortfolio ? 'text-amber-400' : 'text-white'}`}>
            {row.symbol}
          </span>
          {row.alphaToday ? <span className="ml-2 text-[9px] text-emerald-400 uppercase tracking-wide">α</span> : null}
        </td>
        <td className="py-2 px-2 text-[#94a3b8]">{row.sector}</td>
        <td className="py-2 px-2 text-right tabular-nums">{row.q.toFixed(2)}</td>
        <td className="py-2 px-2 text-right tabular-nums">{row.v.toFixed(2)}</td>
        <td className="py-2 px-2 text-right tabular-nums font-semibold">{row.composite.toFixed(2)}</td>
        <td className={`py-2 px-2 text-right tabular-nums font-semibold ${signColor(row.d5.dcomp)}`}>
          {fmtSigned(row.d5.dcomp)}
        </td>
        <td className={`py-2 px-2 text-right tabular-nums ${signColor(row.accel5)}`}>
          {fmtSigned(row.accel5)}
        </td>
        <td className="py-2 px-2 text-right tabular-nums text-[#94a3b8]">
          {row.qShare5 == null ? '—' : `${(row.qShare5 * 100).toFixed(0)}%`}
        </td>
        <td className="py-2 px-2 pr-3 text-right tabular-nums">
          <span className={row.alphaStreak > 0 ? 'text-emerald-400' : 'text-[#475569]'}>{row.alphaStreak}</span>
          {sortVal != null ? <span className="ml-3 text-[#475569]">{expanded ? '▾' : '▸'}</span> : null}
        </td>
      </tr>
      {expanded ? (
        <tr className="bg-[#080d13] border-b border-[#1e293b]">
          <td colSpan={10} className="p-4">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <div className="text-[11px] text-[#64748b] uppercase tracking-wide mb-2">
                  30-day trajectory · {row.name}
                </div>
                <TrajectoryChart trajectory={row.trajectory} />
                <div className="grid grid-cols-3 gap-3 mt-3 text-[11px]">
                  <Mini label="1d Δ comp" value={fmtSigned(row.d1.dcomp)} sign={row.d1.dcomp} />
                  <Mini label="5d Δ comp" value={fmtSigned(row.d5.dcomp)} sign={row.d5.dcomp} />
                  <Mini label="20d Δ comp" value={fmtSigned(row.d20.dcomp)} sign={row.d20.dcomp} />
                </div>
              </div>
              <div>
                <div className="text-[11px] text-[#64748b] uppercase tracking-wide mb-2">Portfolio fit</div>
                <PortfolioFitPanel fit={fit} inPortfolio={inPortfolio} />
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[11px] text-[#64748b] uppercase tracking-wide mb-2">Recent alerts (14d)</div>
              {events == null ? (
                <div className="text-[12px] text-[#475569]">Loading…</div>
              ) : events.length === 0 ? (
                <div className="text-[12px] text-[#475569]">No alerts.</div>
              ) : (
                <ul className="space-y-1 text-[12px]">
                  {events.map((e, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-[#64748b] tabular-nums whitespace-nowrap">{e.ts.slice(0, 10)}</span>
                      <span className={`uppercase tracking-wide text-[10px] font-semibold ${tierColor[e.tier] || 'text-[#94a3b8]'} whitespace-nowrap`}>
                        {e.tier}
                      </span>
                      <span className="text-[#94a3b8]">{e.headline}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Mini({ label, value, sign }: { label: string; value: string; sign: number | null }) {
  return (
    <div>
      <div className="text-[#64748b] uppercase tracking-wide">{label}</div>
      <div className={`text-[13px] font-semibold tabular-nums ${signColor(sign)}`}>{value}</div>
    </div>
  );
}

function signColor(v: number | null | undefined): string {
  if (v == null) return 'text-[#475569]';
  if (v > 0) return 'text-emerald-400';
  if (v < 0) return 'text-red-400';
  return 'text-[#94a3b8]';
}
